import { ReactElement, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton,
  MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AccountBalance, Block, CheckCircleOutline, Close, Download, ErrorOutline, HourglassEmpty, PaidOutlined,
  PlayCircleOutline, Settings, Sync, VerifiedUser, Visibility,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import StatCard from '../../components/ui/StatCard';
import { withSerialNo } from '../../components/ui/gridColumns';
import { formatMoney } from '../../utils/currency';
import { getApiErrorMessage } from '../../services/userService';
import { downloadPayoutCsv, payoutApi, Withdrawal } from '../../services/business/payoutService';
import {
  EMPTY_FILTERS, FilterState, PayoutFilterBar, PromptDialog, PromptField, StatusChip, dateText, filterParams,
  personLabel, titleCase,
} from './payoutShared';

const STATUS_TABS = ['', 'pending', 'approved', 'processing', 'paid', 'rejected', 'failed', 'cancelled'];

type ActionKind = 'reject' | 'markPaid' | 'markFailed' | 'payStripe' | 'approvePay' | 'approveManual';

const PROMPTS: Record<Exclude<ActionKind, 'payStripe' | 'approvePay'>, { title: string; confirm: string; color: 'error' | 'success'; fields: PromptField[] }> = {
  approveManual: {
    title: 'Approve & pay',
    confirm: 'Approve & mark paid',
    color: 'success',
    fields: [{ name: 'transactionRef', label: 'Bank / UTR / NEFT reference (optional)' }],
  },
  reject: {
    title: 'Reject withdrawal',
    confirm: 'Reject & refund',
    color: 'error',
    fields: [{ name: 'reason', label: 'Reason shown to the partner', required: true, minLength: 3, type: 'multiline' }],
  },
  markPaid: {
    title: 'Mark as paid (offline transfer)',
    confirm: 'Mark paid',
    color: 'success',
    fields: [
      { name: 'transactionRef', label: 'Bank / ACH / wire reference', required: true, minLength: 3 },
      {
        name: 'paymentMethod', label: 'Payment method', type: 'select', defaultValue: 'ach',
        options: [
          { value: 'ach', label: 'ACH' }, { value: 'wire', label: 'Wire transfer' }, { value: 'bank_transfer', label: 'Bank transfer' },
          { value: 'check', label: 'Check' }, { value: 'other', label: 'Other' },
        ],
      },
      { name: 'paidAt', label: 'Paid on', type: 'date', defaultValue: new Date().toISOString().slice(0, 10) },
      { name: 'note', label: 'Internal note', type: 'multiline' },
    ],
  },
  markFailed: {
    title: 'Mark as failed',
    confirm: 'Mark failed & refund',
    color: 'error',
    fields: [{ name: 'reason', label: 'Failure reason', required: true, minLength: 3, type: 'multiline' }],
  },
};

export default function PayoutWithdrawalsPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [gateway, setGateway] = useState('');
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [action, setAction] = useState<ActionKind | null>(null);
  const [actionRow, setActionRow] = useState<Withdrawal | null>(null);
  const [actionError, setActionError] = useState('');
  const [flash, setFlash] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const params = { ...filterParams(filters), status: status || undefined, gateway: gateway || undefined, page: page + 1, limit: pageSize };

  const { data, isLoading, error } = useQuery({
    queryKey: ['payout-withdrawals', params],
    queryFn: () => payoutApi.withdrawals(params),
  });
  const { data: summary } = useQuery({ queryKey: ['payout-summary', {}], queryFn: () => payoutApi.summary({}) });
  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['payout-withdrawal', selectedId],
    queryFn: () => payoutApi.withdrawal(selectedId!),
    enabled: !!selectedId,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['payout-withdrawals'] });
    queryClient.invalidateQueries({ queryKey: ['payout-withdrawal'] });
    queryClient.invalidateQueries({ queryKey: ['payout-summary'] });
  };

  const mutation = useMutation({
    mutationFn: async ({ id, kind, values }: { id: string; kind: ActionKind | 'sync'; values?: Record<string, string> }) => {
      if (kind === 'approvePay') return payoutApi.approve(id, {});
      if (kind === 'approveManual') return payoutApi.approve(id, { transactionRef: values?.transactionRef?.trim() || undefined });
      if (kind === 'payStripe') return payoutApi.payStripe(id);
      if (kind === 'sync') return payoutApi.sync(id);
      if (kind === 'reject') return payoutApi.reject(id, values!.reason);
      if (kind === 'markFailed') return payoutApi.markFailed(id, values!.reason);
      return payoutApi.markPaid(id, {
        transactionRef: values!.transactionRef,
        paymentMethod: values!.paymentMethod || undefined,
        paidAt: values!.paidAt ? new Date(values!.paidAt).toISOString() : undefined,
        note: values!.note || undefined,
      });
    },
    onSuccess: (res) => {
      setAction(null);
      setActionRow(null);
      setActionError('');
      setFlash({ type: 'success', text: (res as { data?: { message?: string } }).data?.message || 'Done' });
      refresh();
    },
    onError: (err) => {
      const msg = getApiErrorMessage(err, 'Action failed');
      if (action) setActionError(msg);
      else setFlash({ type: 'error', text: msg });
      refresh();
    },
  });

  const queue = summary?.queues.withdrawals || {};
  const startAction = (kind: ActionKind, row?: Withdrawal) => {
    setActionRow(row || null);
    setActionError('');
    setAction(kind);
  };
  const closeAction = () => { setAction(null); setActionRow(null); };
  const run = (kind: ActionKind | 'sync', values?: Record<string, string>, id = actionRow?._id ?? selectedId) => {
    if (id) mutation.mutate({ id, kind, values });
  };

  const rowActions = (row: Withdrawal) => {
    const stripe = row.gateway === 'stripe';
    const busyRow = mutation.isPending && mutation.variables?.id === row._id;
    const item = (title: string, icon: ReactElement, onClick: () => void, color: 'default' | 'success' | 'error' | 'primary' = 'default') => (
      <Tooltip key={title} title={title}>
        <span>
          <IconButton size="small" color={color} disabled={busyRow} onClick={(e) => { e.stopPropagation(); onClick(); }}>{icon}</IconButton>
        </span>
      </Tooltip>
    );
    const items = [item('View details', <Visibility fontSize="small" />, () => setSelectedId(row._id), 'primary')];
    if (row.status === 'pending' || row.status === 'approved') {
      items.push(
        item(stripe ? 'Approve & pay via Stripe' : 'Approve & pay', <CheckCircleOutline fontSize="small" />, () => startAction(stripe ? 'approvePay' : 'approveManual', row), 'success'),
        item('Reject & refund', <Block fontSize="small" />, () => startAction('reject', row), 'error'),
      );
    } else if (row.status === 'processing') {
      if (stripe) items.push(item('Sync with Stripe', <Sync fontSize="small" />, () => run('sync', undefined, row._id)));
      else items.push(item('Mark paid', <PaidOutlined fontSize="small" />, () => startAction('markPaid', row), 'success'));
      items.push(item('Mark failed & refund', <ErrorOutline fontSize="small" />, () => startAction('markFailed', row), 'error'));
    } else if (row.status === 'paid' && stripe) {
      items.push(item('Check transfer on Stripe', <Sync fontSize="small" />, () => run('sync', undefined, row._id)));
    }
    return <Stack direction="row" alignItems="center" height="100%">{items}</Stack>;
  };

  const columns: GridColDef<Withdrawal>[] = [
    { field: 'requestNumber', headerName: 'Request #', width: 150 },
    { field: 'createdAt', headerName: 'Requested', width: 165, valueFormatter: (v) => dateText(v as string) },
    { field: 'user', headerName: 'Partner', flex: 1, minWidth: 150, valueGetter: (_v, row) => personLabel(row.user) },
    { field: 'userType', headerName: 'Type', width: 120, valueFormatter: (v) => titleCase(v as string) },
    { field: 'amount', headerName: 'Amount', width: 110, valueFormatter: (v, row) => formatMoney(v, row.currency) },
    { field: 'netAmount', headerName: 'Net', width: 110, valueFormatter: (v, row) => formatMoney(v, row.currency) },
    {
      field: 'account', headerName: 'Pay to', width: 170,
      valueGetter: (_v, row) => row.accountSnapshot?.type === 'stripe_connect'
        ? `Stripe ${row.accountSnapshot?.accountNumberMasked || ''}`
        : `${row.accountSnapshot?.bankName || 'Bank'} ${row.accountSnapshot?.accountNumberMasked || ''}`,
    },
    {
      field: 'gateway', headerName: 'Route', width: 140,
      renderCell: ({ row }) => (
        <Stack direction="row" gap={0.5} alignItems="center" height="100%">
          <Chip size="small" variant="outlined" label={row.gateway === 'stripe' ? 'Stripe' : 'Manual'} />
          {row.source === 'scheduled'
            ? <Chip size="small" color="secondary" label="Scheduled" />
            : row.autoApproved && <Chip size="small" color="info" label="Auto" />}
        </Stack>
      ),
    },
    { field: 'status', headerName: 'Status', width: 120, renderCell: ({ value }) => <StatusChip status={value as string} /> },
    {
      field: 'actions', headerName: 'Actions', width: 150, sortable: false, filterable: false,
      renderCell: ({ row }) => rowActions(row),
    },
  ];

  const d = detail;
  const isStripe = d?.gateway === 'stripe';
  const target = actionRow ?? d;
  const busy = mutation.isPending;

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance"
        title="Partner Withdrawals"
        subtitle="Review, approve and pay partner and provider withdrawal requests. Rejected or failed requests are refunded to the partner wallet automatically."
        actions={(
          <>
            <Button variant="outlined" startIcon={<Download />} onClick={() => downloadPayoutCsv('/payouts/admin/withdrawals', { ...params }, 'withdrawals')}>
              Export CSV
            </Button>
            <Button variant="outlined" startIcon={<Settings />} component={RouterLink} to="/payouts/settings">Settings</Button>
          </>
        )}
      />

      {flash && <Alert severity={flash.type} onClose={() => setFlash(null)} sx={{ mb: 2 }}>{flash.text}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(error)}</Alert>}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Pending review" value={queue.pending?.count ?? 0} hint={formatMoney(queue.pending?.amount ?? 0)} icon={<HourglassEmpty />} color="#f59e0b" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Approved, unpaid" value={queue.approved?.count ?? 0} hint={formatMoney(queue.approved?.amount ?? 0)} icon={<PlayCircleOutline />} color="#0284c7" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Processing" value={queue.processing?.count ?? 0} hint={formatMoney(queue.processing?.amount ?? 0)} icon={<Sync />} color="#6366f1" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="KYC to review" value={summary?.queues.kycPending ?? 0} icon={<VerifiedUser />} color="#16a34a" to="/payouts/kyc" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Bank accounts to verify" value={summary?.queues.accountsPending ?? 0} icon={<AccountBalance />} color="#0f766e" to="/payouts/accounts" />
        </Grid>
      </Grid>

      <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
        {STATUS_TABS.map((s) => (
          <Chip
            key={s || 'all'}
            label={s ? titleCase(s) : 'All'}
            clickable
            color={status === s ? 'primary' : 'default'}
            variant={status === s ? 'filled' : 'outlined'}
            onClick={() => { setStatus(s); setPage(0); }}
          />
        ))}
      </Stack>

      <PayoutFilterBar
        value={filters}
        onChange={(f) => { setFilters(f); setPage(0); }}
        extra={(
          <TextField select size="small" label="Route" value={gateway} onChange={(e) => { setGateway(e.target.value); setPage(0); }} sx={{ minWidth: 140 }}>
            <MenuItem value="">All routes</MenuItem>
            <MenuItem value="stripe">Stripe</MenuItem>
            <MenuItem value="manual">Manual bank</MenuItem>
          </TextField>
        )}
      />

      {data?.meta?.totals && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {data.meta.total} requests · Requested {formatMoney(data.meta.totals.amount)} · Fees {formatMoney(data.meta.totals.fee)} · Net {formatMoney(data.meta.totals.net)}
        </Typography>
      )}

      <DataGrid
        rows={data?.rows || []}
        columns={withSerialNo(columns)}
        loading={isLoading}
        getRowId={(r) => r._id}
        autoHeight
        paginationMode="server"
        rowCount={data?.meta?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
        pageSizeOptions={[25, 50, 100]}
        onRowDoubleClick={({ row }) => setSelectedId(row._id)}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />

      <Dialog open={!!selectedId} onClose={() => setSelectedId(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box flex={1}>Withdrawal {d?.requestNumber || ''}</Box>
          {d && <StatusChip status={d.status} />}
          <IconButton onClick={() => setSelectedId(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !d ? <Typography>Loading…</Typography> : (
            <Stack gap={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="overline">Amount</Typography>
                    <Typography variant="h5" fontWeight={800}>{formatMoney(d.amount, d.currency)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fee {formatMoney(d.fee, d.currency)} · Partner receives <b>{formatMoney(d.netAmount, d.currency)}</b>
                    </Typography>
                    <Typography variant="body2" mt={1}>
                      {d.source === 'scheduled' ? 'Scheduled auto-payout' : `Mode: ${titleCase(d.mode)}${d.autoApproved ? ' (auto-approved)' : ''}`}
                    </Typography>
                    <Typography variant="body2">
                      Route: {!isStripe ? 'Manual bank transfer' : d.accountSnapshot?.stripePayoutMethodId ? 'Stripe bank payout' : 'Stripe Connect transfer'}
                    </Typography>
                    {d.transactionRef && <Typography variant="body2">Reference: <b>{d.transactionRef}</b></Typography>}
                    {d.gatewayStatus && <Typography variant="body2">Stripe status: {titleCase(d.gatewayStatus)}</Typography>}
                    {(d.rejectionReason || d.failureReason) && (
                      <Alert severity="error" sx={{ mt: 1 }}>{d.rejectionReason || d.failureReason}</Alert>
                    )}
                    {d.refundedAt && <Alert severity="info" sx={{ mt: 1 }}>Refunded to wallet on {dateText(d.refundedAt)}</Alert>}
                    {d.partnerNote && <Typography variant="body2" mt={1}>Partner note: {d.partnerNote}</Typography>}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="overline">Partner</Typography>
                    <Typography fontWeight={700}>{personLabel(d.user)}</Typography>
                    <Typography variant="body2" color="text.secondary">{titleCase(d.userType)} · {d.user?.phone || d.user?.email}</Typography>
                    <Typography variant="body2" mt={1}>Wallet balance now: <b>{formatMoney(d.wallet?.balance ?? 0, d.wallet?.currency)}</b>{d.wallet?.isFrozen ? ' (frozen)' : ''}</Typography>
                    <Typography variant="body2">KYC: <StatusChip status={d.kyc?.status} /></Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="overline">Pay to</Typography>
                    <Typography variant="body2">{d.accountSnapshot?.accountHolderName}</Typography>
                    <Typography variant="body2">
                      {d.accountSnapshot?.type === 'stripe_connect' ? 'Stripe connected account' : d.accountSnapshot?.bankName || 'Bank account'}
                      {' '}{d.accountSnapshot?.accountNumberMasked}
                      {d.accountSnapshot?.routingNumber ? ` · Routing ${d.accountSnapshot.routingNumber}` : ''}
                    </Typography>
                    {d.account && <Typography variant="body2">Account status: <StatusChip status={d.account.status} /></Typography>}
                    {d.accountSnapshot?.type === 'bank' && (
                      <Button size="small" component={RouterLink} to={`/payouts/accounts?userId=${d.userId}`} sx={{ mt: 0.5 }}>
                        View full account number
                      </Button>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>Timeline</Typography>
                <Stack gap={0.75}>
                  {d.timeline.map((t, i) => (
                    <Stack key={i} direction="row" gap={1} alignItems="center">
                      <StatusChip status={t.status} />
                      <Typography variant="body2" color="text.secondary">{dateText(t.at)} · {titleCase(t.actorType)}</Typography>
                      {t.note && <Typography variant="body2">— {t.note}</Typography>}
                    </Stack>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>Audit log</Typography>
                <Stack gap={0.5}>
                  {d.logs.map((l) => (
                    <Typography key={l._id} variant="body2" color="text.secondary">
                      {dateText(l.createdAt)} · <b>{l.action}</b> by {l.actorId ? personLabel(l.actorId) : titleCase(l.actorType)}
                      {l.message ? ` — ${l.message}` : ''}{l.ip ? ` (${l.ip})` : ''}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        {d && (
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1, p: 2 }}>
            {(d.status === 'pending' || d.status === 'approved') && (
              <>
                <Button color="error" onClick={() => startAction('reject')} disabled={busy}>Reject</Button>
                <Button
                  variant="contained" color="success" disabled={busy}
                  onClick={() => startAction(isStripe ? 'approvePay' : 'approveManual')}
                >
                  {isStripe ? 'Approve & pay via Stripe' : 'Approve & pay'}
                </Button>
              </>
            )}
            {d.status === 'processing' && (
              <>
                {isStripe && <Button startIcon={<Sync />} onClick={() => run('sync')} disabled={busy}>Sync with Stripe</Button>}
                {!isStripe && <Button variant="contained" color="success" onClick={() => startAction('markPaid')} disabled={busy}>Mark paid</Button>}
                <Button color="error" onClick={() => startAction('markFailed')} disabled={busy}>Mark failed</Button>
              </>
            )}
            {d.status === 'paid' && isStripe && (
              <Button startIcon={<Sync />} onClick={() => run('sync')} disabled={busy}>Check transfer on Stripe</Button>
            )}
          </DialogActions>
        )}
      </Dialog>

      {action && (action === 'payStripe' || action === 'approvePay') && target && (
        <Dialog open onClose={() => !busy && closeAction()}>
          <DialogTitle>Send {formatMoney(target.netAmount, target.currency)} via Stripe?</DialogTitle>
          <DialogContent>
            <Typography>
              Stripe sends {formatMoney(target.netAmount, target.currency)} from your Stripe payouts balance to {personLabel(target.user)}&apos;s
              bank account {target.accountSnapshot?.accountNumberMasked || ''}, and the withdrawal is marked paid. If the bank rejects it later,
              the amount goes back to the partner&apos;s wallet automatically.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAction} disabled={busy}>Cancel</Button>
            <Button variant="contained" disabled={busy} onClick={() => { run(action); closeAction(); }}>
              {busy ? 'Sending…' : 'Send transfer'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {action && action !== 'payStripe' && action !== 'approvePay' && (
        <PromptDialog
          open
          title={PROMPTS[action].title}
          confirmLabel={PROMPTS[action].confirm}
          confirmColor={PROMPTS[action].color}
          fields={PROMPTS[action].fields}
          busy={busy}
          error={actionError}
          description={(action === 'markPaid' || action === 'approveManual') && target ? (
            <Alert severity="info">
              Send {formatMoney(target.netAmount, target.currency)} to {target.accountSnapshot?.bankName || 'the partner\'s bank'} {target.accountSnapshot?.accountNumberMasked || ''}
              {' '}from your bank. The withdrawal is marked paid now.
            </Alert>
          ) : undefined}
          onClose={closeAction}
          onSubmit={(values) => run(action, values)}
        />
      )}
    </PageShell>
  );
}
